"use strict";

const CustomEsriLayerManagement = function () {
  const key = "esri-ref-layers";
  const option = "Reference Layers";
  const iconClass = "layers";
  const FORM_KEY = "esri-layer-form";
  const MAX_LAYERS = 3;
  // Reference layers aren't relevant to non-map tools, so exclude them from the module list.
  const EXCLUDED_MODULES = ["report", "scenario", "outmon", "peo", "trashram"];

  let $page;
  let props = {};
  let popupRows = [];

  function _getModulesOptions() {
    return [
      { value: "all", name: "All" },
      ...ToolFunctions.getToolsInOrder()
        .filter((tool) => !EXCLUDED_MODULES.includes(tool.internalName))
        .map((tool) => ({ value: tool.internalName, name: tool.name })),
    ];
  }

  function loadListeners() {
    $page = SettingsModalController.getParentPage();

    $page.off("click", ".add-layer-button", _showAddLayerModal);
    $page.on("click", ".add-layer-button", _showAddLayerModal);

    $page.off("click", ".edit-layer-button", _showEditLayerModal);
    $page.on("click", ".edit-layer-button", _showEditLayerModal);

    $page.off("click", ".layer-delete-button", _deleteLayerConfirm);
    $page.on("click", ".layer-delete-button", _deleteLayerConfirm);
  }

  function _renderLayerModal(layer) {
    const isEdit = layer !== null;
    const initialUrl = layer ? layer.serviceUrl || "" : "";
    popupRows = isEdit && layer && layer.popupParameters ? [...layer.popupParameters] : [];

    const $modal = $("#esri-layer-modal");
    $modal.html(
      nunjucks.render("modals/settingsModal/customEsriLayerForm.njk", {
        layer: layer,
        isEdit: isEdit,
        modulesOptions: _getModulesOptions(),
      }),
    );
    Form.initializeAndLoadListeners($modal, FORM_KEY);
    _renderPopupRows();

    $modal.off("click", ".save-layer-button").on("click", ".save-layer-button", _saveLayer);
    $modal
      .off("click", ".cancel-layer-button")
      .on("click", ".cancel-layer-button", _hideLayerModal);
    $modal.off("click", ".add-popup-row").on("click", ".add-popup-row", _addPopupRow);
    $modal.off("click", ".remove-popup-row").on("click", ".remove-popup-row", _removePopupRow);
    $modal
      .off("blur", 'input[name="service-url"]')
      .on("blur", 'input[name="service-url"]', async function () {
        const url = $(this).val().trim();
        if (!url || url === initialUrl) return;
        await _fetchAndPopulateFields($modal, url);
      });

    $modal.modal("show");
  }

  function _renderPopupRows() {
    $("#esri-layer-modal #popup-params-rows").html(
      nunjucks.render("modals/settingsModal/popupParamRows.njk", { rows: popupRows }),
    );
    if (Form.isInitialized(FORM_KEY)) {
      Form.manuallySetFormDataField(FORM_KEY, ["popupParameters"], popupRows);
    }
  }

  function _showAddLayerModal() {
    if (_getLayerCount() >= MAX_LAYERS) {
      MessageModal.showWarningModal(
        `This group already has the maximum of ${MAX_LAYERS} custom reference layers.`,
      );
      return;
    }
    _renderLayerModal(null);
  }

  function _showEditLayerModal() {
    const layerId = parseInt($(this).data("layer-id"));
    const layer = props.layers.find((l) => l.id === layerId);
    if (!layer) return;
    _renderLayerModal(layer);
  }

  function _updatePopupRows(newRows) {
    popupRows = newRows;
    _renderPopupRows();
  }

  function _addPopupRow() {
    _updatePopupRows([...popupRows, { label: "", value: "" }]);
  }

  function _removePopupRow() {
    const index = parseInt($(this).data("row-index"));
    _updatePopupRows(popupRows.filter((_, i) => i !== index));
  }

  const SYSTEM_FIELD_TYPES = new Set([
    "esriFieldTypeOID",
    "esriFieldTypeGeometry",
    "esriFieldTypeBlob",
    "esriFieldTypeRaster",
    "esriFieldTypeGlobalID",
  ]);

  function _isSystemField(field) {
    return SYSTEM_FIELD_TYPES.has(field.type) || field.name.toLowerCase().startsWith("shape");
  }

  async function _fetchAndPopulateFields($modal, url) {
    const $rows = $modal.find("#popup-params-rows");
    $rows.html('<p class="help-block">Fetching fields from service...</p>');

    try {
      const token = await CustomLayerConfig.getCustomLayerTokenIfNeeded();
      const metadata = await _fetchMetadata(url, token);

      const fields = CustomLayerConfig.isServiceRootMetadata(metadata)
        ? await _fetchSublayerFields(url, metadata, token)
        : metadata.fields || [];

      popupRows = fields
        .filter((field) => !_isSystemField(field))
        .map((field) => ({ label: field.alias || field.name, value: field.name, isFetched: true }));
      _renderPopupRows();
    } catch (_e) {
      $rows.html(
        '<p class="text-danger small">Could not fetch fields from this URL. You can add rows manually.</p>',
      );
    }
  }

  async function _fetchMetadata(url, token) {
    const params = new URLSearchParams({ f: "json" });
    if (token) params.append("token", token);

    const response = await Network.fetchFrom(`${url}?${params}`);
    return response.json();
  }

  // A service root has no fields of its own, so offer the fields shared by every sublayer, which
  // are the only ones a popup can render for all of them.
  async function _fetchSublayerFields(url, serviceMetadata, token) {
    const sublayerMetadata = await Promise.all(
      serviceMetadata.layers.map((sublayer) => _fetchMetadata(`${url}/${sublayer.id}`, token)),
    );

    const [firstSublayer, ...remainingSublayers] = sublayerMetadata;
    return (firstSublayer?.fields || []).filter((field) =>
      remainingSublayers.every((sublayer) =>
        (sublayer.fields || []).some((other) => other.name === field.name),
      ),
    );
  }

  function _getPopupParameters(formData) {
    return (formData.popupParameters ?? [])
      .map((row) => ({
        label: (row?.label ?? "").trim(),
        value: (row?.value ?? "").trim(),
        isFetched: row?.isFetched === true || row?.isFetched === "true",
      }))
      .filter((row) => row.label || row.value);
  }

  async function _saveLayer() {
    const isEdit = $(this).data("is-edit");
    const layerId = $(this).data("layer-id");
    const formData = Form.getDataFromForm(FORM_KEY, false);
    const existing = isEdit ? props.layers.find((l) => l.id === layerId) : null;

    const modules = formData.modules ?? existing?.modules;
    if (!modules || modules.length === 0) {
      MessageModal.showWarningModal("Please select at least one module.");
      return;
    }

    const payload = {
      layer_name: formData.layerName ?? existing?.layerName,
      service_url: formData.serviceUrl ?? existing?.serviceUrl,
      modules: modules,
      popup_parameters: _getPopupParameters(formData),
    };

    try {
      if (isEdit) {
        await ApiCalls.updateCustomEsriLayer(layerId, payload);
      } else {
        await ApiCalls.createCustomEsriLayer(payload);
      }
      _hideLayerModal();
      await rerenderLayerList();
    } catch (e) {
      const errors = e?.responseJSON?.errors;
      const message = errors
        ? Object.values(errors).flat().join("\n")
        : (e?.responseJSON?.message ?? "An error occurred. Please try again.");
      MessageModal.showWarningModal(message);
    }
  }

  function _deleteLayerConfirm() {
    const layerId = parseInt($(this).closest("tr, [data-layer-id]").data("layer-id"));
    const layer = props.layers.find((l) => l.id === layerId);
    const name = layer ? layer.layerName : "this layer";

    MessageModal.showConfirmWarningModal(
      null,
      async () => {
        await ApiCalls.deleteCustomEsriLayer(layerId);
        await rerenderLayerList();
      },
      "Cancel",
      "Delete",
      `<p>Are you sure you want to delete the reference layer <strong>${name}</strong>?</p>`,
      () => {},
      "Delete Reference Layer",
      "modal-sm",
    );
  }

  function _hideLayerModal() {
    Form.clearForm(FORM_KEY);
    $("#esri-layer-modal").modal("hide");
  }

  function _getLayerCount() {
    return props.layers.length;
  }

  function _getModuleNames(moduleValues) {
    const modulesOptions = _getModulesOptions();
    return (moduleValues ?? [])
      .map((value) => modulesOptions.find((option) => option.value === value)?.name ?? value)
      .join(", ");
  }

  async function getProps() {
    const settings = ToolSettings.getSetting("customEsriLayerSettings");
    const layers = ((settings && settings.layers) || []).map((layer) => ({
      ...layer,
      moduleNames: _getModuleNames(layer.modules),
    }));
    props = { layers: layers, layerCount: layers.length, maxLayers: MAX_LAYERS };
    return props;
  }

  function getMenuKey() {
    return key;
  }

  function getMenuOption() {
    return option;
  }

  function getMenuIcon() {
    return iconClass;
  }

  async function rerenderLayerList() {
    await ToolSettings.requestSettings();
    await getProps();
    const existingProps = SettingsModalController.getExistingProps();
    existingProps.customEsriLayerSettings = { layers: props.layers };
    SettingsModalController.updateExistingProps(existingProps);

    $page
      .find(".layer-count-text")
      .text(`${props.layerCount} / ${props.maxLayers} layers configured`);

    $("#esri-layers-tbody").html(
      nunjucks.render("modals/settingsModal/customEsriLayerManagementDatum.njk", {
        props: { layers: props.layers },
      }),
    );

    const $addButton = $page.find(".add-layer-button");
    $addButton.prop("disabled", _getLayerCount() >= MAX_LAYERS);
  }

  function onShow() {
    return;
  }

  function onClose() {
    _hideLayerModal();
  }

  return {
    getProps,
    getMenuKey,
    getMenuOption,
    getMenuIcon,
    onClose,
    onShow,
    loadListeners,
    _getPopupParameters,
    _getModuleNames,
  };
};

module.exports = CustomEsriLayerManagement();

const ApiCalls = require("../../apiCalls");
const CustomLayerConfig = require("../../mapFunctions/customLayerConfig");
const Form = require("../../general/form");
const MessageModal = require("../../modals/messageModal");
const Network = require("../../network");
const SettingsModalController = require("../settingsModalController");
const ToolFunctions = require("../../toolFunctions");
const ToolSettings = require("../toolSettings");
